import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronUp, Check } from 'lucide-react'
import { cm } from '~/utils/utils'
import { Typography } from '~/components/common/Typography'
import { Switch } from '~/components/common/Switch'

interface BaseProps {
  icon?: React.ReactNode
  label: string
  className?: string
  hideBorder?: boolean
}

export interface DropdownOption {
  key: string
  label: string
}

interface SelectionProps extends BaseProps {
  variant: 'selection'
  value: string
  options?: DropdownOption[]
  onSelect?: (key: string) => void
}

interface BinaryProps extends BaseProps {
  variant: 'binary'
  description?: string
  isSelected: boolean
  onChange: (checked: boolean) => void
}

interface ExpandableProps extends BaseProps {
  variant: 'expandable'
  description?: string
  isExpanded?: boolean
  onToggle?: () => void
}

export type SettingsControlItemProps = SelectionProps | BinaryProps | ExpandableProps

export function SettingsControlItem(props: SettingsControlItemProps) {
  const { variant, icon, label, className, hideBorder } = props

  const showBottomBorder = !hideBorder && variant !== 'expandable'

  return (
    <div
      className={cm(
        'flex items-center justify-between w-full select-none',
        showBottomBorder && 'border-b border-tokens-neutral-border-2',
        className
      )}
    >
      <div className='flex items-center gap-4 min-w-0'>
        {icon && (
          <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
            {icon}
          </span>
        )}
        <div className='flex flex-col min-w-0 px-2'>
          {variant === 'binary' ? (
            <>
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                fontWeight='medium'
                className='text-tokens-neutral-text-primary'
              >
                {label}
              </Typography>
              {props.description && (
                <Typography
                  fontFamily='geist'
                  fontSize='labelSm'
                  className='text-tokens-neutral-text-tertiary'
                >
                  {props.description}
                </Typography>
              )}
            </>
          ) : variant === 'expandable' ? (
            <>
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                fontWeight='medium'
                className='text-tokens-neutral-text-primary'
              >
                {label}
              </Typography>
              {props.description && (
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMdMedium'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-tertiary'
                >
                  {props.description}
                </Typography>
              )}
            </>
          ) : (
            <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]'>
              {label}
            </span>
          )}
        </div>
      </div>

      <div className='flex items-center shrink-0'>
        {variant === 'selection' && (
          <InlineDropdown value={props.value} options={props.options} onSelect={props.onSelect} />
        )}

        {variant === 'binary' && (
          <Switch
            isSelected={props.isSelected}
            onChange={props.onChange}
            className='flex-row w-auto gap-0'
          />
        )}

        {variant === 'expandable' && (
          <button
            type='button'
            onClick={props.onToggle}
            className='flex items-center justify-center p-2 cursor-pointer hover:bg-tokens-neutral-fill-fill-6 rounded-lg transition-colors'
          >
            {props.isExpanded ? (
              <ChevronUp className='w-4 h-4 text-tokens-neutral-icon-solid' />
            ) : (
              <ChevronDown className='w-4 h-4 text-tokens-neutral-icon-solid' />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

function InlineDropdown({
  value,
  options,
  onSelect
}: {
  value: string
  options?: DropdownOption[]
  onSelect?: (key: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY - 32,
        left: rect.right + window.scrollX - 223,
        width: 223
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY - 32,
          left: rect.right + window.scrollX - 223,
          width: 223
        })
      }
    }
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const hasOptions = options && options.length > 0

  const handleTriggerClick = () => {
    if (hasOptions) {
      setIsOpen(!isOpen)
    } else if (onSelect) {
      onSelect(value)
    }
  }

  const handleOptionSelect = (key: string) => {
    setIsOpen(false)
    onSelect?.(key)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        onClick={handleTriggerClick}
        className='flex items-center gap-2 h-12 py-2 px-1 rounded-xl justify-end cursor-pointer hover:opacity-80 transition-opacity select-none'
      >
        <span className='font-mono font-semibold text-[15px] leading-normal text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis'>
          {value}
        </span>
        <ChevronDown className='w-[18px] h-[18px] text-tokens-neutral-icon-solid' />
      </button>

      {isOpen &&
        hasOptions &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            className='absolute bg-white rounded-[16px] shadow-[0px_6px_16px_rgba(0,0,0,0.08),0px_3px_6px_rgba(0,0,0,0.12),0px_9px_28px_rgba(0,0,0,0.05)] z-[9999] p-2 flex flex-col gap-2 max-h-[300px] overflow-y-auto'
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              visibility: dropdownPosition.width > 0 ? 'visible' : 'hidden'
            }}
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            {options!.map(option => {
              const isSelected = option.label === value || option.key === value
              return (
                <div
                  key={option.key}
                  className={cm(
                    'h-8 shrink-0 w-full rounded-lg gap-2 truncate px-3 flex items-center justify-between',
                    'text-[14px] font-medium font-sans select-none text-[rgba(0,0,0,0.88)]',
                    'hover:bg-[rgba(0,0,0,0.04)] cursor-pointer transition-colors',
                    isSelected && 'text-[#65558F]'
                  )}
                  onMouseDown={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleOptionSelect(option.key)
                  }}
                >
                  <span className='truncate min-w-0'>{option.label}</span>
                  {isSelected && <Check className='w-4 h-4 shrink-0 text-[#65558F]' />}
                </div>
              )
            })}
          </div>,
          document.body
        )}
    </>
  )
}
